import { render } from "preact";
import { render as jsx_to_string } from "preact-render-to-string";
import { useEffect, useMemo, useState } from "preact/hooks";
import { JSX } from "preact/jsx-runtime";
import Cart, { useCart } from "./cart";
import { show_phone_number_dialog } from "./change_phone";
import * as common from "./common";
import { UNAUTHORIZED, get_error, url } from "./common";
import * as login from "./login";

import { access_t, date_t, member_t, membership_t } from "frontend_common";
import { Trans } from "react-i18next";
import { Translator, useTranslation } from "./i18n";
import {
    LoadCurrentAccessInfo,
    LoadCurrentMemberGroups,
    LoadCurrentMemberInfo,
    LoadCurrentMembershipInfo,
} from "./member_common";
import {
    FindWellKnownProduct,
    LoadProductData,
    Product,
    ProductData,
    RelevantProducts,
    extractRelevantProducts,
    initializeStripe,
    isStripeConfigured,
} from "./payment_common";
import { Sidebar } from "./sidebar";
import {
    SubscriptionInfo,
    SubscriptionType,
    activateSubscription,
    cancelSubscription,
    getCurrentSubscriptions,
} from "./subscriptions";
import { UnitNames, translateUnit } from "./translations";
import { URL_CALENDLY_BOOK, URL_MEMBERBOOTH, URL_SLACK_SIGNUP } from "./urls";
declare var UIkit: any;

type fn_past_enddate = (enddate: date_t, remaining_days: number) => JSX.Element;
type fn_expired_yesterday = () => JSX.Element;
type fn_less_than_one_day_left = (remaining_hours: number) => JSX.Element;
type fn_less_than_two_weeks_left = (
    enddate: date_t,
    remaining_days: number,
) => JSX.Element;
type fn_lots_of_time_left = (
    enddate: date_t,
    remaining_days: number,
) => JSX.Element;
type fn_inactive = () => JSX.Element;

type template_strings_t = [
    fn_past_enddate,
    fn_expired_yesterday,
    fn_less_than_one_day_left,
    fn_less_than_two_weeks_left,
    fn_lots_of_time_left,
    fn_inactive,
];

type membership_info_t = {
    enddate: date_t | null;
    active: boolean;
};

const webshop_url = url("/shop");

function get_random_pin_code(numbers: number): string {
    return Math.random().toString(10).substr(2, numbers);
}

function Info({
    info,
    template_strings,
}: {
    info: membership_info_t;
    template_strings: template_strings_t;
}) {
    let text: JSX.Element;
    let icon: string;
    let color: string;

    const millisecondsPerHour = 1000 * 3600;
    const millisecondsPerDay = millisecondsPerHour * 24;

    if (info.active && info.enddate) {
        const end = Date.parse(info.enddate) + millisecondsPerDay;
        const remainingDays = Math.floor(
            (end - Date.now()) / millisecondsPerDay,
        );

        if (remainingDays < -1) {
            text = template_strings[0](info.enddate, -remainingDays);
            icon = "close";
            color = "member-key-color-inactive";
        } else if (remainingDays < 0) {
            text = template_strings[1]();
            icon = "close";
            color = "member-key-color-inactive";
        } else if (remainingDays < 1) {
            const remainingHours = Math.ceil(
                (end - Date.now()) / millisecondsPerHour,
            );
            text = template_strings[2](remainingHours);
            icon = "check";
            color = "member-key-color-warning";
        } else if (remainingDays < 14) {
            text = template_strings[3](info.enddate, remainingDays);
            icon = "check";
            color = "member-key-color-warning";
        } else {
            text = template_strings[4](info.enddate, remainingDays);
            icon = "check";
            color = "member-key-color-active";
        }
    } else {
        text = template_strings[5]();
        icon = "close";
        color = "member-key-color-inactive";
    }

    return (
        <div class="member-key-box">
            <div
                class={`uk-icon-small member-key-icon ${color}`}
                uk-icon={icon}
            ></div>
            <div class="member-key-status">{text}</div>
        </div>
    );
}

function Info2({
    translation_key,
    info,
}: {
    translation_key: "labaccess" | "membership" | "special_labaccess";
    info: membership_info_t;
}) {
    const { t } = useTranslation("member_page");
    let text: JSX.Element | string;
    let icon: string;
    let color: string;

    const millisecondsPerHour = 1000 * 3600;
    const millisecondsPerDay = millisecondsPerHour * 24;

    if (info.active && info.enddate) {
        const end = Date.parse(info.enddate) + millisecondsPerDay;
        const remainingDays = Math.floor(
            (end - Date.now()) / millisecondsPerDay,
        );

        if (remainingDays < -1) {
            text = t(
                `old_membership_status.${translation_key}.inactive_recent`,
                { days: -remainingDays },
            );
            icon = "close";
            color = "member-key-color-inactive";
        } else if (remainingDays < 0) {
            text = t(
                `old_membership_status.${translation_key}.inactive_yesterday`,
            );
            icon = "close";
            color = "member-key-color-inactive";
        } else if (remainingDays < 1) {
            const remainingHours = Math.ceil(
                (end - Date.now()) / millisecondsPerHour,
            );
            text = t(
                `old_membership_status.${translation_key}.active_hours_remaining`,
                { hours: remainingHours },
            );
            icon = "check";
            color = "member-key-color-warning";
        } else if (remainingDays < 14) {
            text = t(
                `old_membership_status.${translation_key}.active_few_days_remaining`,
                { end_date: info.enddate, days: remainingDays },
            );
            icon = "check";
            color = "member-key-color-warning";
        } else {
            text = t(
                `old_membership_status.${translation_key}.active_days_remaining`,
                { end_date: info.enddate, days: remainingDays },
            );
            icon = "check";
            color = "member-key-color-active";
        }
    } else {
        text = t(`old_membership_status.${translation_key}.inactive`);
        icon = "close";
        color = "member-key-color-inactive";
    }

    return (
        <div class="member-key-box">
            <div
                class={`uk-icon-small member-key-icon ${color}`}
                uk-icon={icon}
            ></div>
            <div class="member-key-status">{text}</div>
        </div>
    );
}

function WarningItem({ children }: { children: preact.ComponentChildren }) {
    return (
        <p>
            <i class="member-text-warning" uk-icon="warning"></i> &nbsp;{" "}
            {children}
        </p>
    );
}

function SignedContractWarning({ member }: { member: member_t }) {
    return member.labaccess_agreement_at ? null : (
        <WarningItem>
            <Trans
                i18nKey="member_page:labaccess_warnings.sign_contract"
                components={[<a href={URL_CALENDLY_BOOK} />, <strong />]}
            />
        </WarningItem>
    );
}

function NoMembershipWarning({ membership }: { membership: membership_t }) {
    return membership.membership_active ? null : (
        <WarningItem>
            <Trans
                i18nKey="member_page:labaccess_warnings.no_membership"
                components={[<a href={webshop_url} />, <strong />]}
            />
        </WarningItem>
    );
}

function MissingPhoneNumber({ member }: { member: member_t }) {
    return member.phone ? null : (
        <WarningItem>
            <Trans
                i18nKey="member_page:labaccess_warnings.missing_phone_number"
                components={[<strong />]}
            />
        </WarningItem>
    );
}

function MissingAccessyInvite({
    membership,
    access,
    member,
    onSendAccessyInvite,
}: {
    membership: membership_t;
    access: access_t;
    member: member_t;
    onSendAccessyInvite: () => void;
}) {
    const { t } = useTranslation("member_page");
    if (!membership.labaccess_active && !membership.special_labaccess_active) {
        // Accessy is not relevant if the member does not have makerspace access
        return null;
    }
    if (access.in_org) {
        // Don't need to send an invite if the member is already in the accessy organization
        // Unless the person has reinstalled the app (see #431) and needs to
        // reinvite themselves. But that button is added further down.
        return null;
    }
    if (member.phone === null) {
        // Can't send an invite if the member does not have a phone number
        return null;
    }
    return [
        <WarningItem>{t("send_accessy_invite_msg")}</WarningItem>,
        <WarningItem>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    onSendAccessyInvite();
                }}
                className="uk-button uk-button-primary"
            >
                {t("send_accessy_invite")}
            </button>
        </WarningItem>,
    ];
}

function PendingLabaccessInstructions({
    can_sync_labaccess,
    pending_labaccess_days,
}: {
    can_sync_labaccess: boolean;
    pending_labaccess_days: number;
}) {
    if (!can_sync_labaccess && pending_labaccess_days) {
        return (
            <>
                Du behöver åtgärda ovanstående fel innan din labbaccess kan
                förlängas. När de är åtgärdade kommer den att förlängas med
                <strong>{pending_labaccess_days} dagar</strong>.
            </>
        );
    } else if (can_sync_labaccess && pending_labaccess_days) {
        return (
            <>
                <strong>{pending_labaccess_days} dagar</strong> kommer läggas
                vid nästa synkronisering.
            </>
        );
    } else {
        return null;
    }
}

function Help({
    member,
    membership,
    pending_labaccess_days,
    access,
    onSendAccessyInvite,
}: {
    member: member_t;
    membership: membership_t;
    pending_labaccess_days: number;
    access: access_t;
    onSendAccessyInvite: () => void;
}) {
    const todo_bullets1 = [
        SignedContractWarning({ member }),
        NoMembershipWarning({ membership }),
        MissingPhoneNumber({ member }),
    ].filter((x) => x !== null);

    const todo_bullets2 = [
        MissingAccessyInvite({
            membership,
            access,
            member,
            onSendAccessyInvite,
        }),
    ].filter((x) => x !== null);

    if (todo_bullets1.length === 0 && todo_bullets2.length === 0) {
        return null;
    }

    const { t } = useTranslation("member_page");
    const pending_labaccess_instruction = (
        <PendingLabaccessInstructions
            can_sync_labaccess={todo_bullets1.length == 0}
            pending_labaccess_days={pending_labaccess_days}
        />
    );

    return (
        <fieldset>
            <legend>
                <i uk-icon="info"></i> {t("instructions_to_become_member")}
            </legend>
            {todo_bullets1}
            {todo_bullets2}
            {pending_labaccess_instruction}
        </fieldset>
    );
}

function Accessy({
    member,
    membership,
    onSendAccessyInvite,
}: {
    member: member_t;
    membership: membership_t;
    onSendAccessyInvite: () => void;
}) {
    const { t } = useTranslation("member_page");

    const disabled =
        !member.phone ||
        (!membership.labaccess_active && !membership.special_labaccess_active);
    const accessyInvite = (
        <>
            <p>{t("accessy_invite_info")}</p>
            <p>
                <button
                    disabled={disabled}
                    class="uk-button uk-button-danger"
                    onClick={(e) => {
                        e.preventDefault();
                        onSendAccessyInvite();
                    }}
                >
                    {t("send_accessy_invite")}
                </button>
            </p>
        </>
    );
    return (
        <fieldset>
            <legend>
                <i uk-icon="info"></i> {t("accessy_invite")}
            </legend>
            {accessyInvite}
        </fieldset>
    );
}

function get_pending_labaccess_days(pending_actions_json: any): number {
    let pendingLabaccessDays = 0;
    for (let pending of pending_actions_json.data) {
        if (pending.action.action === "add_labaccess_days") {
            pendingLabaccessDays += pending.action.value;
        }
    }
    return pendingLabaccessDays;
}

async function change_pin_code(member: member_t) {
    const pin_code = await UIkit.modal.prompt(
        "Välj en pinkod",
        member.pin_code === null ? get_random_pin_code(4) : member.pin_code,
    );
    if (pin_code === null) return;

    try {
        await common.ajax(
            "POST",
            `${window.apiBasePath}/member/current/set_pin_code`,
            { pin_code: pin_code },
        );
        await UIkit.modal.alert(`<h2>Pinkoden är nu bytt</h2>`);
        location.reload();
    } catch (e) {
        UIkit.modal.alert(
            `<h2>Kunde inte byta pinkod</h2><b class="uk-text-danger"">${get_error(
                e,
            )}</b>`,
        );
    }
}

async function change_phone_number(
    member: member_t,
    membership: membership_t,
    t: Translator<"change_phone">,
) {
    const r = await show_phone_number_dialog(
        member.member_id,
        async () => await UIkit.modal.prompt(t("new_number_prompt")),
        async () => await UIkit.modal.prompt(t("validation_code_prompt")),
        t,
    );
    switch (r) {
        case "ok":
            await UIkit.modal.alert(`<h2>Telefonnummret är nu bytt</h2>`);

            if (
                membership.labaccess_active ||
                membership.special_labaccess_active
            ) {
                // Send accessy invite to the new phone number
                await common.ajax(
                    "POST",
                    `${window.apiBasePath}/webshop/member/current/accessy_invite`,
                );
            }

            location.reload();
            break;
        case "cancel":
            break;
        case UNAUTHORIZED:
            login.redirect_to_member_page();
            break;
    }
}

async function send_accessy_invite() {
    try {
        await common.ajax(
            "POST",
            `${window.apiBasePath}/webshop/member/current/accessy_invite`,
        );
        UIkit.modal.alert(`<h2>Inbjudan skickad</h2>`);
    } catch (e) {
        UIkit.modal.alert(
            `<h2>Inbjudan misslyckades</h2><b class="uk-text-danger"">${get_error(
                e,
            )}</b>`,
        );
    }
}

function MembershipView({
    membership,
}: {
    member: member_t;
    membership: membership_t;
    pendingLabaccessDays: number;
}) {
    const labaccessStrings: template_strings_t = [
        (enddate: string, days: number) => (
            <>
                Din <strong>labaccess</strong> är ogiltig sedan {days} dagar (
                {enddate}
                ). <br />
                Your <strong>lab membership</strong> expired {days} day(s) ago (
                {enddate}).
            </>
        ),
        () => (
            <>
                Din <strong>labaccess</strong> gick ut igår. <br />
                Your <strong>lab membership</strong> expired yesterday.
            </>
        ),
        (hours: number) => (
            <>
                Din <strong>labaccess</strong> är giltig i mindre än {hours}{" "}
                timmar till. <br />
                Your <strong>lab membership</strong> is valid for {hours} more
                hours.
            </>
        ),
        (enddate: string, days: number) => (
            <>
                Din <strong>labaccess</strong> är giltig t.o.m. {enddate}{" "}
                (endast {days} dagar till). Kom ihåg att förnya den innan nästa
                fredag morgon. <br />
                Your <strong>lab membership</strong> is valid through {enddate}{" "}
                (only {days} day(s) left). Remember to extend your lab
                membership before next Friday morning.
            </>
        ),
        (enddate: string, days: number) => (
            <>
                Din <strong>labaccess</strong> är giltig t.o.m. {enddate} (
                {days} dagar till). <br />
                Your <strong>lab membership</strong> is valid through {enddate}{" "}
                (only {days} day(s) left).
            </>
        ),
        () => (
            <>
                Din <strong>labaccess</strong> är inaktiv. <br />
                Your <strong>lab membership</strong> is inactive.
            </>
        ),
    ];

    const membershipStrings: template_strings_t = [
        (enddate: string, days: number) => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> är ogiltigt sedan{" "}
                {days} dagar ({enddate}). <br />
                Your <strong>membership</strong> expired {days} day(s) ago (
                {enddate})
            </>
        ),
        () => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> gick ut igår. <br />
                Your <strong>membership</strong> expired yesterday.
            </>
        ),
        (_hours: number) => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> går ut idag. <br />
                Your <strong>membership</strong> expires today.
            </>
        ),
        (enddate: string, days: number) => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> är giltigt t.o.m.{" "}
                {enddate} (endast {days} dagar till). <br />
                Your <strong>membership</strong> is valid through {enddate}{" "}
                (only {days} day(s) left).
            </>
        ),
        (enddate: string, days: number) => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> är giltigt t.o.m.{" "}
                {enddate} ({days} dagar till). <br />
                Your <strong>membership</strong> is valid through {enddate}{" "}
                (only {days} day(s) left).
            </>
        ),
        () => (
            <>
                Ditt <strong>föreningsmedlemsskap</strong> är inaktivt. <br />
                Your <strong>membership</strong> is inactive.
            </>
        ),
    ];

    const specialLabaccessStrings: template_strings_t = [
        (_enddate: string, _days: number) => <></>,
        () => <></>,
        (_hours: number) => <></>,
        (enddate: string, days: number) => (
            <>
                Du har fått <strong>specialtillträde</strong> till
                föreningslokalerna t.o.m. {enddate} ({days} dagar till). <br />
                You have been given <strong>special access</strong> to the
                premises through {enddate} ({days} day(s) left).
            </>
        ),
        (enddate: string, days: number) => (
            <>
                Du har fått <strong>specialtillträde</strong> till
                föreningslokalerna t.o.m. {enddate} ({days} dagar till). <br />
                You have been given <strong>special access</strong> to the
                premises through {enddate} ({days} day(s) left).
            </>
        ),
        () => <></>,
    ];

    return (
        <fieldset class="data-uk-margin">
            <legend>
                <i uk-icon="lock"></i> Medlemsskap
            </legend>
            <Info
                info={{
                    active: membership.membership_active,
                    enddate: membership.membership_end,
                }}
                template_strings={membershipStrings}
            />
            <Info
                info={{
                    active: membership.labaccess_active,
                    enddate: membership.labaccess_end,
                }}
                template_strings={labaccessStrings}
            />
            {membership.special_labaccess_active ? (
                <Info
                    info={{
                        active: membership.special_labaccess_active,
                        enddate: membership.special_labaccess_end,
                    }}
                    template_strings={specialLabaccessStrings}
                />
            ) : null}
        </fieldset>
    );
}

function PersonalData({
    member,
    onChangePinCode,
    onChangePhoneNumber,
}: {
    member: member_t & { has_password: boolean };
    onChangePinCode: () => void;
    onChangePhoneNumber: () => void;
}) {
    const pin_warning =
        member.pin_code === null ? (
            <label class="uk-form-label" style="color: red;">
                <Trans
                    i18nKey="member_page:pin_code_not_set_warning"
                    components={[<a href={URL_MEMBERBOOTH} />]}
                />
            </label>
        ) : null;
    const [showPinCode, setShowPinCode] = useState(false);
    const { t } = useTranslation("member_page");
    return (
        <fieldset>
            <legend>
                <i uk-icon="user"></i> {t("personal_data")}
            </legend>
            <div>
                <label for="firstname" class="uk-form-label">
                    {t("first_name")}
                </label>
                <input
                    name="firstname"
                    class="uk-input readonly-input"
                    value={member.firstname || ""}
                    disabled
                />
            </div>
            <div>
                <label for="lastname" class="uk-form-label">
                    {t("last_name")}
                </label>
                <input
                    name="lastname"
                    class="uk-input readonly-input"
                    value={member.lastname || ""}
                    disabled
                />
            </div>
            <div>
                <label for="email" class="uk-form-label">
                    {t("email")}
                </label>
                <input
                    name="email"
                    class="uk-input readonly-input"
                    value={member.email || ""}
                    disabled
                />
            </div>
            <div>
                <label for="phone" class="uk-form-label">
                    {t("phone_number")}
                </label>
                <span style="width: 100%; display: flex;">
                    <input
                        name="phone"
                        class="uk-input readonly-input"
                        value={member.phone || ""}
                        disabled
                    />
                    <button
                        class="uk-button uk-button-danger"
                        onClick={(e) => {
                            e.preventDefault();
                            onChangePhoneNumber();
                        }}
                    >
                        {t("change_phone_number")}
                    </button>
                </span>
            </div>
            <div>
                <label for="pin_code" class="uk-form-label">
                    {t("pin_code")}
                </label>
                <span style="width: 100%; display: flex;">
                    <input
                        name="pin_code"
                        type={showPinCode ? "text" : "password"}
                        class="uk-input readonly-input"
                        style="white-space: nowrap; font-family: monospace;"
                        value={member.pin_code || ""}
                        disabled
                    />
                    <button
                        class="uk-icon-button uk-margin-small-left uk-margin-small-right"
                        uk-icon={showPinCode ? "eye" : "eye-slash"}
                        onClick={(e) => {
                            setShowPinCode(!showPinCode);
                            e.preventDefault();
                        }}
                    ></button>
                    <button
                        class="uk-button uk-button-danger"
                        onClick={(e) => {
                            e.preventDefault();
                            onChangePinCode();
                        }}
                    >
                        {t("change_pin_code")}
                    </button>
                </span>
                {pin_warning}
            </div>
            <div>
                <label for="password" class="uk-form-label">
                    {t("password")}
                </label>
                <span style="width: 100%; display: flex;">
                    <input
                        name="password"
                        class="uk-input readonly-input"
                        placeholder={
                            member.has_password
                                ? "********"
                                : t("no_password_set")
                        }
                        disabled
                    />
                    <button
                        class="uk-button uk-button-danger"
                        onClick={(e) => {
                            e.preventDefault();
                            common
                                .ajax(
                                    "POST",
                                    `${window.apiBasePath}/oauth/request_password_reset`,
                                    {
                                        user_identification: member.email,
                                        redirect: "member",
                                    },
                                )
                                .then(() => {
                                    UIkit.modal.alert(
                                        t("set_password_alert", {
                                            email: member.email,
                                        }),
                                    );
                                })
                                .catch((e) => {
                                    UIkit.modal.alert(
                                        t("failed_set_password_alert", {
                                            error: get_error(e),
                                        }),
                                    );
                                });
                        }}
                    >
                        {member.has_password
                            ? t("change_password")
                            : t("set_password")}
                    </button>
                </span>
            </div>
        </fieldset>
    );
}

function Address({ member }: { member: member_t }) {
    const { t } = useTranslation("member_page");
    return (
        <fieldset data-uk-margin>
            <legend>
                <i uk-icon="home"></i> {t("address.address_title")}
            </legend>
            <div>
                <label for="address_street" class="uk-form-label">
                    {t("address.address")}
                </label>
                <input
                    name="address_street"
                    class="uk-input readonly-input"
                    value={member.address_street || ""}
                    disabled
                />
            </div>
            <div>
                <label for="address_extra" class="uk-form-label">
                    {t("address.address_co")}
                </label>
                <input
                    name="address_extra"
                    class="uk-input readonly-input"
                    value={member.address_extra || ""}
                    disabled
                />
            </div>
            <div>
                <label for="address_zipcode" class="uk-form-label">
                    {t("address.zip_code")}
                </label>
                <input
                    name="address_zipcode"
                    class="uk-input readonly-input"
                    value={member.address_zipcode || ""}
                    disabled
                />
            </div>
            <div>
                <label for="address_city" class="uk-form-label">
                    {t("address.postal_city")}
                </label>
                <input
                    name="address_city"
                    class="uk-input readonly-input"
                    value={member.address_city || ""}
                    disabled
                />
            </div>
        </fieldset>
    );
}

function SlackSettings({ member }: { member: { email: string } }) {
    const { t } = useTranslation("member_page");
    const [slackEnabled, setSlackEnabled] = useState<boolean>(false);
    const [hasSlackAccount, setHasSlackAccount] = useState<boolean>(false);
    const [taskDelegationEnabled, setTaskDelegationEnabled] =
        useState<boolean>(true);
    const [loading, setLoading] = useState(true);
    const [slackOverrideEmail, setSlackOverrideEmail] = useState<string | null>(
        null,
    );
    const [effectiveSlackEmail, setEffectiveSlackEmail] = useState<string>("");
    const [slackUsername, setSlackUsername] = useState<string | null>(null);
    const [editingEmail, setEditingEmail] = useState(false);
    const [newSlackEmail, setNewSlackEmail] = useState("");
    const [saving, setSaving] = useState(false);

    const loadSlackInfo = () => {
        setLoading(true);
        Promise.all([
            common.ajax(
                "GET",
                `${window.apiBasePath}/member/current/slack_status`,
                null,
            ),
            common.ajax(
                "GET",
                `${window.apiBasePath}/member/current/slack_email`,
                null,
            ),
        ])
            .then(
                ([statusResponse, emailResponse]: [
                    common.ServerResponse<{
                        slack_enabled: boolean;
                        has_slack_account: boolean;
                        task_delegation_enabled: boolean;
                    }>,
                    common.ServerResponse<{
                        slack_override_email: string | null;
                        effective_slack_email: string;
                        slack_username: string | null;
                    }>,
                ]) => {
                    setSlackEnabled(statusResponse.data.slack_enabled);
                    setHasSlackAccount(statusResponse.data.has_slack_account);
                    setTaskDelegationEnabled(
                        statusResponse.data.task_delegation_enabled,
                    );
                    setSlackOverrideEmail(
                        emailResponse.data.slack_override_email,
                    );
                    setEffectiveSlackEmail(
                        emailResponse.data.effective_slack_email,
                    );
                    setSlackUsername(emailResponse.data.slack_username);
                    setLoading(false);
                },
            )
            .catch((e) => {
                console.error("Failed to load Slack info:", e);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadSlackInfo();
    }, []);

    const handleTaskDelegationChange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const newEnabled = target.checked;

        try {
            await common.ajax(
                "POST",
                `${window.apiBasePath}/member/current/task_delegation_enabled`,
                { enabled: newEnabled },
            );
            setTaskDelegationEnabled(newEnabled);
        } catch (e) {
            console.error("Failed to update task delegation setting:", e);
            UIkit.modal.alert(
                `<h2>Failed to update setting</h2><b class="uk-text-danger">${get_error(e)}</b>`,
            );
            // Revert the checkbox
            target.checked = !newEnabled;
        }
    };

    const handleSaveSlackEmail = async () => {
        if (!newSlackEmail.trim()) {
            return;
        }

        setSaving(true);
        try {
            const response = (await common.ajax(
                "POST",
                `${window.apiBasePath}/member/current/slack_email`,
                { slack_email: newSlackEmail.trim() },
            )) as unknown as common.ServerResponse<{
                status?: string;
                slack_email?: string;
            }>;

            // Check if verification was sent
            if (response.data && response.data.status === "verification_sent") {
                // Show modal instructing user to check Slack
                UIkit.modal.alert(
                    `<h2>${t("slack.verification_sent_title" as any)}</h2>` +
                        `<p>${t("slack.verification_sent_message" as any, { email: response.data.slack_email } as any)}</p>` +
                        `<p class="uk-text-muted">${t("slack.verification_check_slack" as any)}</p>`,
                );

                // Close edit mode and reload after a short delay
                setEditingEmail(false);
                setNewSlackEmail("");
                setTimeout(() => {
                    loadSlackInfo();
                }, 2000);
            } else {
                // Legacy response format (shouldn't happen with new code)
                setEditingEmail(false);
                setNewSlackEmail("");
                loadSlackInfo();
            }
        } catch (e) {
            console.error("Failed to save Slack email:", e);
            UIkit.modal.alert(
                `<h2>${t("slack.save_email_error_title")}</h2><b class="uk-text-danger">${get_error(e)}</b>`,
            );
        } finally {
            setSaving(false);
        }
    };

    const handleClearSlackEmail = async () => {
        setSaving(true);
        try {
            await common.ajax(
                "DELETE",
                `${window.apiBasePath}/member/current/slack_email`,
                null,
            );
            loadSlackInfo();
        } catch (e) {
            console.error("Failed to clear Slack email:", e);
            UIkit.modal.alert(
                `<h2>${t("slack.clear_email_error_title")}</h2><b class="uk-text-danger">${get_error(e)}</b>`,
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading || !slackEnabled) {
        return null;
    }

    return (
        <fieldset>
            <legend>
                <i uk-icon="comment"></i> {t("slack.title")}
            </legend>
            <div class="uk-card uk-card-default uk-card-small uk-card-body">
                {hasSlackAccount ? (
                    <>
                        <div class="uk-flex uk-flex-middle uk-margin-small-bottom">
                            <i
                                uk-icon="icon: check; ratio: 0.8"
                                class="uk-text-success"
                            ></i>
                            <span class="uk-margin-small-left">
                                {t("slack.status_connected")}
                            </span>
                        </div>
                        {slackUsername && (
                            <p class="uk-text-small uk-text-muted uk-margin-remove">
                                {t("slack.username_label")}:{" "}
                                <strong>{slackUsername}</strong>
                            </p>
                        )}
                    </>
                ) : (
                    <>
                        <div class="uk-flex uk-flex-middle uk-margin-small-bottom">
                            <i
                                uk-icon="icon: close; ratio: 0.8"
                                class="uk-text-warning"
                            ></i>
                            <span class="uk-margin-small-left uk-text-warning">
                                {t("slack.status_not_connected")}
                            </span>
                        </div>
                        <p class="uk-text-small uk-text-muted uk-margin-small-bottom">
                            {t("slack.slack_description")}
                        </p>
                        <p class="uk-text-small uk-text-muted uk-margin-small-bottom">
                            <strong>
                                {t("slack.email_reminder", {
                                    email: member.email,
                                })}
                            </strong>
                        </p>
                        <a
                            href={URL_SLACK_SIGNUP}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="uk-button uk-button-primary uk-button-small"
                        >
                            {t("slack.join_button")}
                        </a>
                    </>
                )}
            </div>
            <div class="uk-margin-top">
                <div class="uk-card uk-card-default uk-card-small uk-card-body">
                    <h6 class="uk-margin-remove-top uk-margin-small-bottom">
                        {t("slack.email_header")}
                    </h6>
                    <p class="uk-text-small uk-text-muted uk-margin-small-bottom">
                        {t("slack.email_description")}
                    </p>
                    <p class="uk-text-small uk-margin-small-bottom">
                        <strong>{t("slack.current_email_label")}:</strong>{" "}
                        {effectiveSlackEmail}
                    </p>
                    {slackOverrideEmail && (
                        <p class="uk-text-small uk-text-muted uk-margin-small-bottom">
                            {t("slack.using_override_notice")}
                        </p>
                    )}
                    {editingEmail ? (
                        <div class="uk-margin-small-top">
                            <input
                                type="email"
                                class="uk-input uk-form-small uk-margin-small-bottom"
                                placeholder={t("slack.email_placeholder")}
                                value={newSlackEmail}
                                onInput={(e) =>
                                    setNewSlackEmail(
                                        (e.target as HTMLInputElement).value,
                                    )
                                }
                            />
                            <div class="uk-flex uk-flex-middle">
                                <button
                                    class="uk-button uk-button-primary uk-button-small"
                                    onClick={handleSaveSlackEmail}
                                    disabled={saving || !newSlackEmail.trim()}
                                >
                                    {saving
                                        ? t("common.saving")
                                        : t("common.save")}
                                </button>
                                <button
                                    class="uk-button uk-button-default uk-button-small uk-margin-small-left"
                                    onClick={() => {
                                        setEditingEmail(false);
                                        setNewSlackEmail("");
                                    }}
                                    disabled={saving}
                                >
                                    {t("common.cancel")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div class="uk-flex uk-flex-middle">
                            <button
                                class="uk-button uk-button-small uk-button-default"
                                onClick={() => {
                                    setEditingEmail(true);
                                    setNewSlackEmail(slackOverrideEmail || "");
                                }}
                                disabled={saving}
                            >
                                {slackOverrideEmail
                                    ? t("slack.change_email_button")
                                    : t("slack.set_email_button")}
                            </button>
                            {slackOverrideEmail && (
                                <button
                                    class="uk-button uk-button-small uk-button-default uk-margin-small-left"
                                    onClick={handleClearSlackEmail}
                                    disabled={saving}
                                >
                                    {t("slack.clear_email_button")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {hasSlackAccount && (
                <div class="uk-margin-top">
                    <div class="uk-card uk-card-default uk-card-small uk-card-body">
                        <h6 class="uk-margin-remove-top uk-margin-small-bottom">
                            {t("slack.task_delegation_header")}
                        </h6>
                        <p class="uk-text-small uk-text-muted uk-margin-small-bottom">
                            {t("slack.task_delegation_description")}
                        </p>
                        <label>
                            <input
                                type="checkbox"
                                class="uk-checkbox"
                                checked={taskDelegationEnabled}
                                onChange={handleTaskDelegationChange}
                            />{" "}
                            {t("slack.task_delegation_enabled")}
                        </label>
                    </div>
                </div>
            )}
        </fieldset>
    );
}

function SubscriptionPayment({
    sub,
    membership,
    subscriptions,
    member,
    type,
    productData,
}: {
    sub: SubscriptionInfo | null;
    membership: membership_t;
    subscriptions: SubscriptionInfo[];
    member: member_t;
    type: SubscriptionType;
    productData: ProductData;
}) {
    const product = FindWellKnownProduct(
        productData.products,
        `${type}_subscription`,
    )!;
    const { t } = useTranslation("member_page");
    const { t: tCommon } = useTranslation("common");

    return sub ? (
        <div class="uk-button-group">
            <button class="uk-button" disabled>
                {t("subscriptions.auto_renewal_active")}
            </button>
            <button
                class="uk-button uk-button-danger"
                onClick={(e) => {
                    e.preventDefault();
                    cancelSubscription(type, membership, subscriptions);
                }}
            >
                {tCommon("cancel")}
            </button>
        </div>
    ) : (
        <>
            <button
                class="uk-button uk-button-primary"
                onClick={(e) => {
                    e.preventDefault();
                    if (member.labaccess_agreement_at === null) {
                        UIkit.modal.alert(
                            t("subscriptions.errors.no_member_introduction", {
                                calendar_url: URL_CALENDLY_BOOK,
                            }),
                        );
                        return;
                    }

                    activateSubscription(
                        type,
                        member,
                        membership,
                        subscriptions,
                        t,
                    );
                }}
            >
                {t("subscriptions.activate_auto_renewal", {
                    price: product.price,
                    unit: translateUnit(product.unit, 1, tCommon),
                })}
            </button>
        </>
    );
}

function SubscriptionProduct({
    product,
    cart,
    onChangeCart,
}: {
    product: Product;
    cart: Cart;
    onChangeCart: (cart: Cart) => void;
}) {
    const { t } = useTranslation("member_page");
    const { t: tCommon } = useTranslation("common");
    if (
        product.unit !== "mån" &&
        product.unit !== "år" &&
        product.unit !== "st"
    )
        throw new Error(
            `Unexpected unit '${product.unit}' for ${product.name}. Expected one of år/mån/st`,
        );

    return (
        <button
            class="uk-button uk-button-primary"
            onClick={(e) => {
                e.preventDefault();
                cart.setItem(product.id, cart.getItemCount(product.id) + 1);
                onChangeCart(cart);
            }}
        >
            {t("subscriptions.add_to_cart", {
                count: product.smallest_multiple,
                unit: tCommon(`unit.${UnitNames[product.unit]}`, {
                    count: product.smallest_multiple,
                }),
                price: Number(product.price) * product.smallest_multiple,
            })}
        </button>
    );
}

function BaseMembership({
    member,
    membership,
    subscriptions,
    relevantProducts,
    cart,
    onChangeCart,
    productData,
}: {
    member: member_t;
    membership: membership_t;
    subscriptions: SubscriptionInfo[];
    relevantProducts: RelevantProducts;
    cart: Cart;
    onChangeCart: (cart: Cart) => void;
    productData: ProductData;
}) {
    const { t } = useTranslation("member_page");
    const sub = subscriptions.find((sub) => sub.type === "membership") || null;
    return (
        <fieldset>
            <legend>
                <i uk-icon="home"></i> {t("base_membership.title")}
            </legend>
            <Info2
                info={{
                    active: membership.membership_active,
                    enddate: membership.membership_end,
                }}
                translation_key="membership"
            />
            <p>{t("subscriptions.descriptions.membership")}</p>
            <hr />
            <SubscriptionPayment
                type="membership"
                sub={sub}
                membership={membership}
                subscriptions={subscriptions}
                member={member}
                productData={productData}
            />
            <div class="uk-margin-small-top" />
            {sub && sub.upcoming_invoice && (
                <p>
                    {t("subscriptions.next_charge", {
                        amount: sub.upcoming_invoice.amount_due,
                        formattedDate: common.formatDate(
                            sub.upcoming_invoice.payment_date,
                        ),
                    })}
                </p>
            )}
            {!sub && (
                <SubscriptionProduct
                    product={relevantProducts.baseMembershipProduct}
                    cart={cart}
                    onChangeCart={onChangeCart}
                />
            )}
        </fieldset>
    );
}

function MakerspaceAccess({
    member,
    membership,
    subscriptions,
    relevantProducts,
    pending_labaccess_days,
    cart,
    onChangeCart,
    productData,
}: {
    member: member_t;
    membership: membership_t;
    pending_labaccess_days: number;
    subscriptions: SubscriptionInfo[];
    relevantProducts: RelevantProducts;
    cart: Cart;
    onChangeCart: (cart: Cart) => void;
    productData: ProductData;
}) {
    const { t } = useTranslation("member_page");
    const sub = subscriptions.find((sub) => sub.type === "labaccess") || null;
    return (
        <fieldset>
            <legend>
                <i uk-icon="home"></i> {t("makerspace_access.title")}
            </legend>
            <Info2
                info={{
                    active: membership.labaccess_active,
                    enddate: membership.labaccess_end,
                }}
                translation_key="labaccess"
            />
            {membership.special_labaccess_active && (
                <Info2
                    info={{
                        active: membership.special_labaccess_active,
                        enddate: membership.special_labaccess_end,
                    }}
                    translation_key="special_labaccess"
                />
            )}
            {pending_labaccess_days > 0 && (
                <p>
                    {t("subscriptions.pending_makerspace_access", {
                        pending_days: pending_labaccess_days,
                    })}
                </p>
            )}
            <p>{t("subscriptions.descriptions.labaccess")}</p>
            <hr />
            <SubscriptionPayment
                type="labaccess"
                sub={sub}
                membership={membership}
                subscriptions={subscriptions}
                member={member}
                productData={productData}
            />
            <div class="uk-margin-small-top" />
            {sub && sub.upcoming_invoice && (
                <p>
                    {t("subscriptions.next_charge", {
                        amount: sub.upcoming_invoice.amount_due,
                        formattedDate: common.formatDate(
                            sub.upcoming_invoice.payment_date,
                        ),
                    })}
                </p>
            )}
            {!sub && (
                <SubscriptionProduct
                    product={relevantProducts.labaccessProduct}
                    cart={cart}
                    onChangeCart={onChangeCart}
                />
            )}
        </fieldset>
    );
}

function Billing() {
    const { t } = useTranslation("member_page");
    return (
        <fieldset>
            <legend>
                <i uk-icon="credit-card"></i> {t("billing.title")}
            </legend>
            <button
                className="uk-button"
                onClick={async (e) => {
                    e.preventDefault();
                    const url = (
                        await common.ajax(
                            "GET",
                            `${window.apiBasePath}/webshop/member/current/stripe_customer_portal`,
                        )
                    ).data;
                    location.href = url;
                }}
            >
                {t("billing.manage")}
            </button>
        </fieldset>
    );
}

function useFetchRepeatedly<T>(
    fetcher: () => Promise<T>,
    callback: (t: T) => void,
) {
    useEffect(() => {
        let i = 0;
        let id: NodeJS.Timeout;
        const f = async () => {
            callback(await fetcher());
            i++;
            id = setTimeout(
                f,
                Math.min(60 * 60 * 1000, Math.pow(1.5, i) * 1000),
            );
        };
        id = setTimeout(f, 1000);
        return () => clearInterval(id);
    }, []);
}

function MemberPage({
    member,
    membership: initial_membership,
    pending_labaccess_days,
    productData,
    subscriptions,
    show_beta,
    access,
}: {
    member: member_t & { has_password: boolean };
    membership: membership_t;
    pending_labaccess_days: number;
    subscriptions: SubscriptionInfo[];
    show_beta: boolean;
    productData: ProductData;
    access: access_t;
}) {
    const apiBasePath = window.apiBasePath;
    const [membership, setMembership] = useState(initial_membership);
    const relevantProducts = useMemo(
        () => extractRelevantProducts([...productData.id2item.values()]),
        [productData.id2item],
    );

    // Automatically refresh membership info.
    // This is particularly useful when the user has had the tab open for a long time.
    useFetchRepeatedly(LoadCurrentMembershipInfo, setMembership);
    const { t } = useTranslation("member_page");
    const { t: tPhone } = useTranslation("change_phone");
    const { cart, setCart } = useCart();

    return (
        <>
            <Sidebar
                cart={cart.items.length > 0 ? { cart, productData } : null}
            />
            <div id="content">
                <form class="uk-form uk-form-stacked uk-margin-bottom">
                    <h2>
                        {t("member")} {member.member_number}: {member.firstname}{" "}
                        {member.lastname}
                    </h2>
                    {show_beta ? (
                        <>
                            <Help
                                member={member}
                                membership={membership}
                                pending_labaccess_days={pending_labaccess_days}
                                access={access}
                                onSendAccessyInvite={send_accessy_invite}
                            />
                            <BaseMembership
                                member={member}
                                membership={membership}
                                subscriptions={subscriptions}
                                relevantProducts={relevantProducts}
                                cart={cart}
                                onChangeCart={setCart}
                                productData={productData}
                            />
                            <MakerspaceAccess
                                member={member}
                                membership={membership}
                                subscriptions={subscriptions}
                                relevantProducts={relevantProducts}
                                cart={cart}
                                onChangeCart={setCart}
                                productData={productData}
                                pending_labaccess_days={pending_labaccess_days}
                            />
                            {isStripeConfigured() && <Billing />}
                        </>
                    ) : (
                        <>
                            <MembershipView
                                member={member}
                                membership={membership}
                                pendingLabaccessDays={pending_labaccess_days}
                            />
                            <Help
                                member={member}
                                membership={membership}
                                pending_labaccess_days={pending_labaccess_days}
                                access={access}
                                onSendAccessyInvite={send_accessy_invite}
                            />
                        </>
                    )}
                    <PersonalData
                        member={member}
                        onChangePinCode={() => void change_pin_code(member)}
                        onChangePhoneNumber={() =>
                            void change_phone_number(member, membership, tPhone)
                        }
                    />
                    <Address member={member} />
                    <SlackSettings member={member} />
                    <Accessy
                        member={member}
                        membership={membership}
                        onSendAccessyInvite={send_accessy_invite}
                    />
                </form>
            </div>
        </>
    );
}

common.documentLoaded().then(() => {
    common.addSidebarListeners();
    if (isStripeConfigured()) initializeStripe();

    const apiBasePath = window.apiBasePath;
    const root = document.querySelector("#root") as HTMLElement;

    const future1 = LoadCurrentMemberInfo();
    const membership = LoadCurrentMembershipInfo();
    const future3 = common.ajax(
        "GET",
        apiBasePath + "/webshop/member/current/pending_actions",
        null,
    );
    const subscriptions = getCurrentSubscriptions().then((s) =>
        s.filter((sub) => sub.active),
    );
    const groups = LoadCurrentMemberGroups();
    const productData = LoadProductData();
    const access = LoadCurrentAccessInfo();

    Promise.all([
        future1,
        membership,
        future3,
        subscriptions,
        groups,
        productData,
        access,
    ])
        .then(
            ([
                member,
                membership,
                pending_actions_json,
                subscriptions,
                _groups,
                productData,
                access,
            ]) => {
                const pending_labaccess_days =
                    get_pending_labaccess_days(pending_actions_json);
                const in_beta_group = true;
                if (root != null) {
                    render(
                        <MemberPage
                            member={member}
                            membership={membership}
                            pending_labaccess_days={pending_labaccess_days}
                            subscriptions={subscriptions}
                            show_beta={in_beta_group}
                            productData={productData}
                            access={access}
                        />,
                        root,
                    );
                }
            },
        )
        .catch((e) => {
            // Probably Unauthorized, redirect to login page.
            if (e.status === UNAUTHORIZED) {
                // Render login
                login.render_login(root, null, null);
            } else {
                console.log(e);
                UIkit.modal.alert(
                    jsx_to_string(
                        <>
                            <h2>Failed to load member info</h2>
                            <pre>{JSON.stringify(e)}</pre>
                        </>,
                    ),
                );
            }
        });
});
